import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import useGameSounds from "./useGameSounds";
import useKidVoice from "./useKidVoice";

/**
 * useIdleNudge — gently re-engages a child who has stopped interacting.
 *
 * After `idleMs` with no tap / key / touch, Cabil plays a soft chime and
 * speaks a friendly "are you still here?" line using the child's name. It
 * keeps nudging on each idle stretch, but stops after `maxNudges` so it never
 * becomes annoying, and pauses while the tab is hidden.
 *
 * Speech relies on the Web Speech API, which browsers (esp. iOS Safari) only
 * allow after the page has had a user gesture — which it always has by the
 * time a child is inside the kids section, so the nudge will speak.
 *
 * @param {string} childName  the child's name (falls back to "friend")
 * @param {object} opts       { idleMs = 35000, maxNudges = 3, enabled = true }
 */
export default function useIdleNudge(childName, opts = {}) {
  const { idleMs = 35000, maxNudges = 3, enabled = true } = opts;

  const { t }     = useTranslation();
  const sounds    = useGameSounds();
  const { speak } = useKidVoice();

  // Keep latest values without retriggering the effect (the sound hooks return
  // a fresh object every render).
  const ref = useRef({ sounds, speak, childName, idleMs, maxNudges, enabled, t });
  ref.current = { sounds, speak, childName, idleMs, maxNudges, enabled, t };

  useEffect(() => {
    let timer   = null;
    let nudges  = 0;

    // Admin-editable via the Translations page (voice.idleNudge1-4).
    const phrases = (name, tr) => [
      tr("voice.idleNudge1", { name }),
      tr("voice.idleNudge2", { name }),
      tr("voice.idleNudge3", { name }),
      tr("voice.idleNudge4", { name }),
    ];

    const fire = () => {
      const { sounds: s, speak: sp, childName: cn, maxNudges: max, enabled: on, t: tr } = ref.current;
      if (!on || document.hidden || nudges >= max) return;
      nudges += 1;
      const name = (cn && cn.trim()) || "friend";
      const list = phrases(name, tr);
      const line = list[Math.min(nudges - 1, list.length - 1)];
      try { s.playClick(); } catch { /* ignore */ }
      setTimeout(() => { try { sp(line); } catch { /* ignore */ } }, 250);
      // Schedule the next check (it self-limits via maxNudges).
      schedule();
    };

    const schedule = () => {
      clearTimeout(timer);
      const { idleMs: ms, enabled: on } = ref.current;
      if (on) timer = setTimeout(fire, ms);
    };

    // Any real interaction resets the idle clock and re-arms nudging.
    const onActivity = () => { nudges = 0; schedule(); };

    const onVisibility = () => {
      if (document.hidden) clearTimeout(timer);
      else onActivity();
    };

    const events = ["pointerdown", "keydown", "touchstart", "mousemove"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener("visibilitychange", onVisibility);
    schedule();

    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // arm once per mount; live values come from the ref
}
